import React from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Globe, Menu, X } from 'lucide-react';
import { Logo } from './Logo';

export const Header: React.FC = () => {
  const { language, setLanguage, t } = useLanguage();
  const [isLangMenuOpen, setIsLangMenuOpen] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isScrolled, setIsScrolled] = React.useState(false);
  const location = useLocation();

  const navItems = [
    { path: '/', label: t('home') },
    { path: '/about', label: t('aboutMe') },
    { path: '/blog', label: t('blog') },
    { path: '/multimedia', label: t('multimedia') },
    { path: 'https://clerk.do/', label: t('clerk') },
    { path: '/contact', label: t('contact') },
  ];

  const languages = [
    { code: 'en' as const, label: 'English' },
    { code: 'es' as const, label: 'Español' },
    { code: 'fr' as const, label: 'Français' },
  ];

  // Close mobile menu when route changes
  React.useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Detect scroll position
  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
    <motion.header
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b transition-all duration-300 ${
        isScrolled
          ? 'bg-white/90 border-charcoal/10'
          : 'bg-charcoal/95 border-steel-blue/20'
      }`}
    >
      <div className="container mx-auto px-6 py-3 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-2"
        >
          {/* Logo */}
          <Logo className="h-10 w-auto" inverted={isScrolled} />
          
          {/* Name */}
          <div className="flex flex-col leading-tight">
            <span className={`font-bold text-lg md:text-xl tracking-wide transition-colors duration-300 ${
              isScrolled ? 'text-charcoal' : 'text-white'
            }`}>
              JOAN BISONÓ
            </span>
            <span className={`text-[10px] md:text-xs font-semibold tracking-widest transition-colors duration-300 ${
              isScrolled ? 'text-steel-blue-600' : 'text-steel-blue-200'
            }`}>
              MEJÍA
            </span>
          </div>
        </Link>

          <nav className="flex items-center gap-4">
            {/* Desktop Navigation */}
          <ul className="hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              <li
                  key={item.path}
              >
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `transition-colors text-sm font-medium uppercase tracking-wide ${
                        isScrolled
                          ? isActive || (item.path === '/blog' && location.pathname.startsWith('/blog'))
                            ? 'text-charcoal font-semibold'
                            : 'text-steel-blue-600 hover:text-charcoal'
                          : isActive || (item.path === '/blog' && location.pathname.startsWith('/blog'))
                            ? 'text-white font-semibold'
                            : 'text-steel-blue-200 hover:text-white'
                      }`
                    }
                >
                  {item.label}
                  </NavLink>
              </li>
            ))}
          </ul>

            {/* Language Selector */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
              className={`gap-2 transition-colors ${
                isScrolled
                  ? 'text-steel-blue-600 hover:text-charcoal hover:bg-charcoal/10'
                  : 'text-steel-blue-200 hover:text-white hover:bg-steel-blue/20'
              }`}
            >
              <Globe className="w-4 h-4" />
              <span className="uppercase">{language}</span>
            </Button>

            {isLangMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`absolute top-full right-0 mt-2 rounded-lg shadow-lg border overflow-hidden min-w-[140px] z-50 ${
                  isScrolled
                    ? 'bg-white border-charcoal/20'
                    : 'bg-charcoal-600 border-steel-blue/30'
                }`}
              >
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);
                      setIsLangMenuOpen(false);
                    }}
                    className={`w-full px-4 py-2 text-left transition-colors ${
                      isScrolled
                        ? language === lang.code
                          ? 'bg-steel-blue text-white font-semibold'
                          : 'text-charcoal hover:bg-steel-blue/10 hover:text-steel-blue-700'
                        : language === lang.code
                          ? 'bg-steel-blue text-white font-semibold'
                          : 'text-steel-blue-200 hover:bg-steel-blue/20 hover:text-white'
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </motion.div>
            )}
          </div>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden transition-colors ${
                isScrolled
                  ? 'text-charcoal hover:bg-charcoal/10'
                  : 'text-white hover:bg-steel-blue/20'
              }`}
            >
              {isMobileMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </Button>
        </nav>
      </div>

        {/* Mobile Navigation Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className={`md:hidden overflow-hidden border-t ${
                isScrolled
                  ? 'bg-white border-charcoal/10'
                  : 'bg-charcoal border-steel-blue/20'
              }`}
            >
              <ul className="py-4 space-y-1">
                {navItems.map((item) => (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      className={({ isActive }) =>
                        `block px-6 py-3 transition-colors uppercase text-sm font-medium tracking-wide ${
                          isScrolled
                            ? isActive || (item.path === '/blog' && location.pathname.startsWith('/blog'))
                              ? 'bg-steel-blue text-white font-semibold'
                              : 'text-charcoal hover:bg-steel-blue/10 hover:text-steel-blue-700'
                            : isActive || (item.path === '/blog' && location.pathname.startsWith('/blog'))
                              ? 'bg-steel-blue text-white font-semibold'
                              : 'text-steel-blue-200 hover:bg-steel-blue/20 hover:text-white'
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
    </motion.header>

      {/* Overlay when mobile menu is open */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </>
  );
};
